import React, { useState, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import {
  CreateUserQuery,
  LoginQuery,
  LoginWithFbQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import * as IPurchases from '../../../../../interfaces/merchants/products/purchases';
import {
  setMeToPurchaseInfo,
  setPostable,
} from '../../../../../redux/actions/merchants/products/purchases';
import { getInputs } from '../../../../../redux/selectors/merchants/products/purchases/inputs';
import * as aUser from '../../../../../redux/actions/users/api';

import PurchaseLoginRequireAlert from './components/PurchaseLoginRequireAlert';
import InputCreateUser from './components/CreateUser';
import InputLogin from './components/Login';
import { ReduxState } from '../../../../../redux/reducers';
import CreateUserAlert from '../../../../../molecules/common/CreateUserAlert';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';

interface Props {
  schemeType: IPurchases.SchemeType;
  postable: boolean;
  onSubmit: () => void;
  onSubmitSubscribed?: () => void;
  isEmailVerified: boolean;
  onJustCreateUser?: () => void;
}

enum ViewType {
  Login = 'login',
  CreateUser = 'createUser',
}

const InputRequireLogin: React.FC<Props> = (props) => {
  const {
    schemeType,
    onSubmit,
    onSubmitSubscribed,
    postable,
    isEmailVerified,
    onJustCreateUser,
  } = props;
  const dispatch = useAppDispatch();
  const { login, loginWithFb, createUser } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();

  const [viewType, setViewType] = useState<ViewType>(ViewType.Login);

  const inputs = useSelector(
    (state: ReduxState) => getInputs(state.merchantsProductsPurchasesInputs),
    shallowEqual,
  );

  const handleSwitchView = (viewType: ViewType) => {
    setViewType(viewType);
  };

  const handleLogined = useCallback(async () => {
    await dispatch(setMeToPurchaseInfo(inputs.productId!));
    if (schemeType === IPurchases.SchemeType.Subscription) {
      const userSubscription = await aUser.fetchUsersSubscription(
        inputs.productId!,
      );
      if (userSubscription && onSubmitSubscribed) {
        onSubmitSubscribed();
      } else {
        if (isEmailVerified) {
          onSubmit();
        }
      }
    }
    if (schemeType === IPurchases.SchemeType.TicketBook && isEmailVerified) {
      onSubmit();
    }
  }, [
    dispatch,
    inputs.productId,
    onSubmitSubscribed,
    onSubmit,
    schemeType,
    isEmailVerified,
  ]);

  const handleLogin = useCallback(
    async (_q: LoginQuery) => {
      dispatch(setPostable(false));
      login(_q.email, _q.password)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await handleLogined();
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, handleLogined, login],
  );

  const handleLoginWithFb = useCallback(
    async (query: LoginWithFbQuery) => {
      dispatch(setPostable(false));
      loginWithFb(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);

          await handleLogined();
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [dispatch, handleLogined, loginWithFb],
  );

  const handleCreateUser = useCallback(
    async (query: CreateUserQuery) => {
      dispatch(setPostable(false));
      createUser(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);
          onJustCreateUser?.();
          await handleLogined();
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [createUser, dispatch, handleLogined, onJustCreateUser],
  );

  const labelType =
    schemeType === IPurchases.SchemeType.Subscription
      ? 'subscription'
      : 'ticketBook';

  if (viewType === ViewType.Login)
    return (
      <>
        <PurchaseLoginRequireAlert
          labelType={labelType}
          onClick={() => handleSwitchView(ViewType.CreateUser)}
        />
        <InputLogin
          postable={postable}
          onLogin={(query: LoginQuery) => handleLogin(query)}
          onLoginWithFb={(query: LoginWithFbQuery) => handleLoginWithFb(query)}
          errors={errors}
        />
      </>
    );

  return (
    <>
      <CreateUserAlert onClick={() => handleSwitchView(ViewType.Login)} />
      <InputCreateUser
        postable={postable}
        onSubmit={(query: CreateUserQuery) => handleCreateUser(query)}
        errors={errors}
      />
    </>
  );
};

export default InputRequireLogin;
